import React from 'react';
import { listStateKey, useRememberedFilter } from './listState';

/*
 * Filtro da mettere accanto alla ricerca delle liste (prop `filterControl`): un'etichetta
 * e un chip per ogni valore, piu' "Tutti". Il valore '' vuol dire nessun filtro.
 * Le opzioni sono stringhe oppure { value, label }.
 * Oltre MAX_CHIPS valori i chip occuperebbero troppo spazio: diventa un menu a tendina.
 * Con `asDropdown` e' sempre una tendina (es. Sport, che cresce con i dati).
 *
 * Con `multiple` la scelta e' multipla con il chip «Tutti» in testa (regola SUI 03/10/2026):
 * `value` e' l'elenco dei valori accesi; tutti i valori = nessun filtro (si vede acceso solo «Tutti»).
 */
const MAX_CHIPS = 5;

const normalize = (option) => (typeof option === 'string' ? { value: option, label: option } : option);

export const ChipFilter = ({ label, options, value, onChange, asDropdown = false, multiple = false, withAll = false }) => {
    const items = options.map(normalize);
    useRememberedFilter(listStateKey('filtro', label), value, onChange);

    // Un filtro a scelta multipla resta a chip anche con molte opzioni: la tendina gestisce un solo valore.
    if (!multiple && (asDropdown || items.length > MAX_CHIPS)) {
        return (
            <div className="sid-chips">
                <span className="sid-chips__label">{label}</span>
                <select
                    className={`sid-select sid-chips__select${value ? ' is-active' : ''}`}
                    aria-label={label}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {/* Il nome del filtro non sta più accanto (regola SUI): lo dice la voce «Tutti» */}
                    <option value="">{`${label}: Tutti`}</option>
                    {items.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </select>
            </div>
        );
    }

    /*
     * `withAll` (03/10/2026, da provare sulle strutture sportive e poi estendere): primo chip
     * «Tutti», acceso all'inizio. Scegliendo un valore «Tutti» si spegne e resta acceso solo
     * quel valore; gli altri si aggiungono o si tolgono uno alla volta; «Tutti» spegne tutto il
     * resto e mostra ogni elemento. `value` è l'elenco dei valori scelti: [] = Tutti.
     * I chip accesi sono antracite con l'etichetta bianca.
     */
    if (withAll) {
        const selected = value || [];
        const toggle = (v) => {
            const next = selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v];
            onChange(next);
        };
        return (
            <div className="sid-chips sid-chips--tutti" role="group" aria-label={label}>
                <span className="sid-chips__label">{label}</span>
                <button
                    type="button"
                    className={`sid-chip${selected.length === 0 ? ' is-active' : ''}`}
                    aria-pressed={selected.length === 0}
                    onClick={() => onChange([])}
                    data-filtro={label}
                >
                    Tutti
                </button>
                {items.map((item) => {
                    const active = selected.includes(item.value);
                    return (
                        <button
                            key={item.value}
                            type="button"
                            className={`sid-chip${active ? ' is-active' : ''}`}
                            aria-pressed={active}
                            onClick={() => toggle(item.value)}
                        >
                            {item.label}
                        </button>
                    );
                })}
            </div>
        );
    }

    /*
     * Scelta multipla con il chip «Tutti» (regola SUI del 03/10/2026, come Strutture sportive):
     * per le pagine resta tutto com'era — `value` con TUTTI i valori = nessun filtro — ma si vede
     * acceso solo «Tutti». Scegliendo un valore resta acceso solo quello; gli altri si aggiungono o
     * si tolgono; togliendo l'ultimo, o premendo «Tutti», si torna a tutti.
     */
    if (multiple) {
        const tuttiValori = items.map((item) => item.value);
        const selected = value || [];
        const tutti = selected.length === 0 || tuttiValori.every((v) => selected.includes(v));
        const toggle = (v) => {
            if (tutti) return onChange([v]);
            const next = selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v];
            onChange(next.length === 0 ? tuttiValori : next);
        };
        return (
            <div className="sid-chips sid-chips--tutti" role="group" aria-label={label}>
                <span className="sid-chips__label">{label}</span>
                <button
                    type="button"
                    className={`sid-chip${tutti ? ' is-active' : ''}`}
                    aria-pressed={tutti}
                    onClick={() => onChange(tuttiValori)}
                    data-filtro={label}
                >
                    Tutti
                </button>
                {items.map((item) => {
                    const active = !tutti && selected.includes(item.value);
                    return (
                        <button
                            key={item.value}
                            type="button"
                            className={`sid-chip${active ? ' is-active' : ''}`}
                            aria-pressed={active}
                            onClick={() => toggle(item.value)}
                        >
                            {item.label}
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="sid-chips sid-chips--tutti" role="group" aria-label={label}>
            <span className="sid-chips__label">{label}</span>
            {[{ value: '', label: 'Tutti' }, ...items].map((item) => {
                const active = value === item.value;
                return (
                    <button
                        key={item.value || 'tutti'}
                        type="button"
                        className={`sid-chip${active ? ' is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => onChange(item.value)}
                        data-filtro={item.value === '' ? label : undefined}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
};

/*
 * Filtro dei chip `multiple` sulle righe: passa se il valore e' fra quelli accesi, oppure se
 * non e' fra le opzioni previste (vuoto, sconosciuto). Cosi' con tutti i chip accesi la lista
 * e' davvero completa, e nessuna riga sparisce per uno stato inatteso.
 */
export const filtroMultiplo = (selected, options, value) => {
    const valori = options.map((o) => (typeof o === 'string' ? o : o.value));
    return !valori.includes(value) || selected.includes(value);
};

export default ChipFilter;
